import React from 'react';
import { format } from 'date-fns';
import { Calendar, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGoalsQuery } from '@/hooks/goal-planner/queries/use-goals-query';
import { TaskCardAssignees } from './TaskCardAssignees';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface TaskCardDatesProps {
  dueDate?: string;
  createdDate: Date;
  isCompleted: boolean;
  goalId?: string;
  hideGoalName?: boolean;
  taskId: string;
}

const TaskCardDates = ({ dueDate, createdDate, isCompleted, goalId, hideGoalName = false, taskId }: TaskCardDatesProps) => {
  const { data: goals = [] } = useGoalsQuery();
  const goal = goalId ? goals.find(g => g.id === goalId) : null;
  const { isMobile } = useIsMobile();

  if (isMobile) {
    // Mobile layout: Hide all date/project information
    return null;
  }

  // Desktop layout: Horizontal layout with project first, then date
  return (
    <div className="flex items-center justify-between min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        {!hideGoalName && goal && (
          <div className="flex items-center min-w-0">
            <Target 
              size={14} 
              className={cn(
                "flex-shrink-0",
                isCompleted ? "text-green-600 dark:text-green-400" : "text-purple-600 dark:text-purple-400"
              )} 
            />
            <div className="ml-1.5 min-w-0 max-w-[120px] sm:max-w-none" style={{ overflow: 'hidden' }}>
              <span 
                className={cn(
                  "text-xs font-medium bg-clip-text text-transparent block",
                  isCompleted 
                    ? "bg-gradient-to-r from-green-500 to-emerald-600 dark:from-green-400 dark:to-emerald-500" 
                    : "bg-gradient-to-r from-purple-500 to-blue-600 dark:from-purple-400 dark:to-blue-500"
                )}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={`Project: ${goal.projectTitle || goal.title}`}
              >
                Project: {goal.projectTitle || goal.title}
              </span>
            </div>
          </div>
        )}
        
        {dueDate && (
          <div className="flex items-center">
            <Calendar 
              size={14} 
              className={isCompleted ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"} 
            />
            <span 
              className={cn(
                "ml-1.5 text-xs font-medium bg-clip-text text-transparent",
                isCompleted 
                  ? "bg-gradient-to-r from-green-500 to-emerald-600 dark:from-green-400 dark:to-emerald-500" 
                  : "bg-gradient-to-r from-amber-500 to-yellow-600 dark:from-amber-400 dark:to-yellow-500"
              )}
            >
              Due: {format(new Date(dueDate), 'MMM d')}
            </span>
          </div>
        )}
        
        {hideGoalName && (
          <TaskCardAssignees taskId={taskId} hideWhenEmpty />
        )}
      </div>
    </div>
  );
};

export default TaskCardDates;
